/**
 * The unit vocabularies: `OrgUnit.kind`, `OrgUnit.structure` and
 * `OrgUnitSource` (Issue #13412, #16417; ADR-0100, ADR-0107, ADR-0138).
 *
 * `kind` and `structure` are `String` columns whose closed lists live HERE,
 * not in the database (ADR-0107): an `as const` tuple, a derived `z.enum` and
 * a derived type. CLIENT-SAFE — zod only, no Prisma, no `server-only` — so the
 * terminology layer and the pickers name a kind without pulling the generated
 * client into a browser bundle.
 *
 * The drift test (`tests/unit/lib/org-units/kinds.test.ts`) pins what can
 * still drift: the legacy enum values carried in their stored spelling, the
 * `structure` default, and the `structure` CHECK the database enforces.
 * `OrgUnitSource` is still a Postgres enum and is still compared against it.
 */

import { z } from 'zod'

/**
 * Every unit kind (ADR-0138 § 2).
 *
 * The first seven are the dropped `OrgUnitKind` enum's members in their
 * STORED spelling, so the String conversion rewrote no row. New codes follow
 * the same upper snake case. `ORGANIZATION` is the `isSelf` root's kind and
 * nobody else's; the rest after it are the launch kinds of ADR-0138
 * decision 14 — department and team reuse the existing codes.
 *
 * `kind` is a LABEL. No access rule branches on it (ADR-0100 decision 1);
 * access branches on {@link ORG_UNIT_STRUCTURES}.
 */
export const ORG_UNIT_KINDS = [
  'SCHOOL',
  'CAMPUS',
  'DIVISION',
  'DEPARTMENT',
  'TEAM',
  'REGION',
  'OTHER',
  'ORGANIZATION',
  'POD',
  'LEADERSHIP_TEAM',
  'WORKING_GROUP',
  'COMMITTEE',
  'PLC',
] as const

export const orgUnitKindSchema = z.enum(ORG_UNIT_KINDS)

export type OrgUnitKindName = (typeof ORG_UNIT_KINDS)[number]

/** The kind of the org's root `isSelf` unit, and of no other unit. */
export const ORGANIZATION_UNIT_KIND = 'ORGANIZATION' satisfies OrgUnitKindName

/**
 * The kind a stored value is read as when this release does not know it.
 * The column is a `String`, so a row can carry a kind a LATER release added;
 * reading it as the generic kind degrades a label instead of throwing.
 */
export const FALLBACK_ORG_UNIT_KIND = 'OTHER' satisfies OrgUnitKindName

/**
 * The kinds a person may give a unit from a picker: every kind but the
 * root's. A second `ORGANIZATION` unit would be a second organization.
 */
export const ASSIGNABLE_ORG_UNIT_KINDS = ORG_UNIT_KINDS.filter(
  (kind): kind is Exclude<OrgUnitKindName, typeof ORGANIZATION_UNIT_KIND> =>
    kind !== ORGANIZATION_UNIT_KIND,
)

/**
 * The kinds a person may give a SECONDARY unit — a group started from a
 * Handbook (#16432): the cross-tree kinds of ADR-0138 decision 14. A label
 * only, like every kind; what makes the unit secondary is `structure`.
 */
export const SECONDARY_ORG_UNIT_KINDS = [
  'POD',
  'LEADERSHIP_TEAM',
  'WORKING_GROUP',
  'COMMITTEE',
  'PLC',
] as const satisfies readonly OrgUnitKindName[]

export function isOrgUnitKind(value: unknown): value is OrgUnitKindName {
  return orgUnitKindSchema.safeParse(value).success
}

/** A stored `kind`, narrowed; unknown values read as {@link FALLBACK_ORG_UNIT_KIND}. */
export function toOrgUnitKind(value: string): OrgUnitKindName {
  return isOrgUnitKind(value) ? value : FALLBACK_ORG_UNIT_KIND
}

/**
 * The kind that means "a school", for the code that has to single one out.
 *
 * Named here rather than in each consumer because it had drifted into two
 * private `const SCHOOL_UNIT_KIND = 'SCHOOL'` declarations plus a bare literal,
 * and it decides real behaviour in all three: which units a district rollup
 * counts (`@/lib/actionboard/pillar-school-coverage`) and which unit wins the
 * primary-unit tie-break (`@/lib/viewer/fold-viewer-context`). Widening it in
 * one place and not the other is how a campus outranks the school it belongs
 * to.
 */
export const SCHOOL_UNIT_KIND = 'SCHOOL' satisfies OrgUnitKindName

/**
 * A unit's structure (ADR-0138 § 2).
 *
 * - `primary` units form the strict tree under `parentUnitId` and are what
 *   access and roll-ups are built from.
 * - `secondary` units cross the tree: no parent, exactly one sponsor, an
 *   optional end date, visibility only.
 *
 * The rules that bind the two are `./structure.ts`. Unlike `kind`, the
 * database also refuses an out-of-list value (`org_units_structure_check`),
 * because this vocabulary decides who reads what (ADR-0107 § 4 as refined by
 * ADR-0138).
 */
export const ORG_UNIT_STRUCTURES = ['primary', 'secondary'] as const

export const orgUnitStructureSchema = z.enum(ORG_UNIT_STRUCTURES)

export type OrgUnitStructure = (typeof ORG_UNIT_STRUCTURES)[number]

export const PRIMARY_UNIT_STRUCTURE = 'primary' satisfies OrgUnitStructure
export const SECONDARY_UNIT_STRUCTURE = 'secondary' satisfies OrgUnitStructure

/**
 * The `OrgUnitSource` values, for the drift test. No string-union alias is
 * exported alongside it: nothing needs one yet, and an unused type is a
 * shared primitive that is not shared.
 */
export const ORG_UNIT_SOURCES = [
  'RESEARCH',
  'IMPORT',
  'ROSTER',
  'HRIS',
  'MANUAL',
] as const
